import { createContext, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { routes } from "../routes";
import { SignInType } from "../types/users";
import UsersModel from "../models/users.model";
import LocalStorageService from "../services/localstorage.service";
import { PermissionsType } from "../types/permissions";

interface AuthContextType {
    verified: boolean;
    logout: () => void;
    singIn: (data: SignInType) => any;
    refreshToken: () => any;
    permissions: Array<PermissionsType>;
}

interface Props {
    children: React.ReactElement;
}

const AuthContext = createContext<AuthContextType>({
    verified: false,
    logout: () => {},
    singIn: () => {},
    refreshToken: () => {},
    permissions: []
});

export const AUTH_KEY = "auth.monclova"

export const AuthProvider = ({ children }: Props) => {

  const authData = LocalStorageService.get(AUTH_KEY)
  const currentTime = new Date().getTime();

  const [verified, setVerified] = useState<boolean>(!authData ? false : (authData.expiresAt > currentTime))
  const [permissions, setPermissions] = useState<Array<PermissionsType>>(!authData ? [] : authData.permissions)
  const navigate = useNavigate();

  const refreshToken = async () => {
    if(!authData) return

    const response = await UsersModel.refreshToken()
    if(response.message){
      setVerified(false)
      LocalStorageService.remove(AUTH_KEY)
      return
    }

    const currentDate = new Date().getTime();
    const newTime = currentDate + (response.expires_in! * 1000);
    const newDate = new Date(newTime).getTime();

    LocalStorageService.set(AUTH_KEY, {
        accessToken: response.access_token,
        expiresAt: newDate,
        permissions: response.permissions,
    })
    setPermissions(response.permissions || [])
    setVerified(true)
  }

  const singIn = async (data: SignInType) => {
    const areEmpty = data.email.length === 0 || data.password.length === 0
    const areStrings = typeof data.email === "string" && typeof data.password === "string"
    if(areEmpty || !areStrings) return

    const response = await UsersModel.signIn(data)

    if(response.message){
        setVerified(false)
        LocalStorageService.remove(AUTH_KEY)
        return false
    }

    const currentDate = new Date().getTime();
    const newTime = currentDate + (response.expires_in! * 1000);
    const newDate = new Date(newTime).getTime();

    LocalStorageService.set(AUTH_KEY, {
        accessToken: response.access_token,
        expiresAt: newDate,
        permissions: response.permissions,
    })
    setPermissions(response.permissions || [])
    setVerified(true)
    return true
  }

  const logout = () => {
    LocalStorageService.remove(AUTH_KEY)
    setVerified(false);
    navigate(routes.HOME, { replace: true });
  };
  
  const value: AuthContextType = {
    verified,
    logout,
    singIn,
    refreshToken,
    permissions
  };


  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  return useContext(AuthContext);
};